import { z } from 'zod';
import type { BlockNode, BlockNodeWithMedia, ExperienceDocument, ProgressNode, Size, UrlMediaSource } from './ir.js';

// THE GATEKEEPER. Server-side validation at publish — NOT a source for code generation.
//
// NORMATIVE DEFINITION IS `SPEC.md`. This file enforces it at the trust boundary; the four SDK
// decoders are HAND-WRITTEN and LENIENT (SPEC §9). That asymmetry is deliberate:
//
//   at publish   an unknown key is a TYPO. A typo that a lenient SDK silently ignores is
//                exactly the bug class that stays invisible until a customer reports it.
//   in the field an unknown key is THE FUTURE. Refusing it bricks the experience.
//
// Hence z.strictObject here, and `fallback`-or-drop there.
//
// `blockSchema` is typed AGAINST the hand-written BlockNode union in `ir.ts`, mirroring rules.ts.
// TypeScript therefore PROVES the schema and the type agree — no drift, and no codegen.

// ---------------------------------------------------------------------------
// Bounds. Part of the contract: LOOSENING IS ADDITIVE, TIGHTENING IS BREAKING.
// ---------------------------------------------------------------------------

export const MAX_LAYOUT_DEPTH = 32;
export const MAX_LAYOUT_NODES = 1024;

const MAX_GRID_COLS = 12;
const MAX_PROGRESS_SEGMENTS = 32;
const MAX_SPANS = 64;
const MAX_SPAN_CHARS = 2000;
const MAX_REPEAT = 50;
const MAX_EXTENT = 10_000;
const MAX_LINES = 50;

// Iterative bounds walk over the RAW document, run BEFORE the recursive zod parse: zod's lazy
// union recurses per node, so without this a hostile ~2000-deep document blows the call stack
// (a RangeError escaping even safeParse) and an arbitrarily wide one burns full parse cost
// before the node budget could reject it. Runs on untrusted input.
//
// Follows THREE keys, not one: a node's subtree can hang off `children`, off a `fallback`
// (which may itself carry a fallback), or off a repeat's `template`.
export function checkRawBounds(raw: unknown, ctx: z.RefinementCtx): void {
  let nodes = 0;
  let maxDepth = 0;
  const stack: Array<{ value: unknown; depth: number }> = [{ value: raw, depth: 1 }];

  while (stack.length > 0) {
    const { value, depth } = stack.pop() as { value: unknown; depth: number };
    nodes++;
    if (depth > maxDepth) maxDepth = depth;
    if (nodes > MAX_LAYOUT_NODES || maxDepth > MAX_LAYOUT_DEPTH) break; // cheap early exit
    if (typeof value !== 'object' || value === null) continue;

    const v = value as { children?: unknown; fallback?: unknown; template?: unknown; root?: unknown };
    if (v.root !== undefined) stack.push({ value: v.root, depth });
    if (Array.isArray(v.children)) {
      for (const c of v.children) stack.push({ value: c, depth: depth + 1 });
    }
    if (v.fallback !== undefined) stack.push({ value: v.fallback, depth: depth + 1 });
    if (v.template !== undefined) stack.push({ value: v.template, depth: depth + 1 });
  }

  if (maxDepth > MAX_LAYOUT_DEPTH) {
    ctx.addIssue({ code: 'custom', message: `layout tree deeper than ${MAX_LAYOUT_DEPTH} levels` });
  }
  if (nodes > MAX_LAYOUT_NODES) {
    ctx.addIssue({ code: 'custom', message: `layout tree has more than ${MAX_LAYOUT_NODES} nodes` });
  }
}

// ---------------------------------------------------------------------------
// Sizes. THE LEGALITY TABLE IS A TYPE (SPEC §5.0) — most of the laws cost no refinement.
// ---------------------------------------------------------------------------

const extent = z.number().min(0).max(MAX_EXTENT);
const anySize = z.union([extent, z.literal('fill'), z.literal('hug')]);
const fixedOrFill = z.union([extent, z.literal('fill')]);

/** THE ONE PLACE a `size` is shaped. Five blocks have different WIDTH/HEIGHT legality (below), but
 *  `max_width`/`min_width` are legal on all five — and a strictObject rejects a key it was not
 *  told about, so hand-copying the two bounds into five literals is exactly how one of them ends
 *  up missing and an author's `max_width` fails to publish on `image` alone.
 *
 *  The bounds clamp the RESOLVED width, so they are meaningless against a FIXED width — you have
 *  already said what the width is. That is a typo, and publish is the only place to catch it. */
const sizeOf = <W extends z.ZodType, H extends z.ZodType>(width: W, height: H) =>
  z
    .strictObject({
      width,
      height,
      max_width: extent.optional(),
      min_width: extent.optional(),
    })
    .superRefine((raw, ctx) => {
      // The generic width/height make zod's inferred output opaque here; the four members this
      // refinement actually reads are the same in all five instantiations.
      const s = raw as Pick<Size, 'width' | 'max_width' | 'min_width'>;
      for (const k of ['max_width', 'min_width'] as const) {
        if (s[k] !== undefined && typeof s.width === 'number') {
          ctx.addIssue({
            code: 'custom',
            path: [k],
            message: `${k} is meaningless with a fixed width (${s.width}) — it clamps a width the solver resolves, and this one is already decided`,
          });
        }
      }
      if (s.max_width !== undefined && s.min_width !== undefined && s.min_width > s.max_width) {
        ctx.addIssue({
          code: 'custom',
          path: ['min_width'],
          message: `min_width ${s.min_width} exceeds max_width ${s.max_width} — no width can satisfy both`,
        });
      }
    });

const anySizeSchema = sizeOf(anySize, anySize.optional()).optional();

/** `custom` and `progress` MUST declare an explicit extent. `hug` on either resolves to 0 — and
 *  for `custom` a `hug` would mean measuring a HOST view, which destroys the solver's purity
 *  and headless conformance (SPEC §8). Both are rejected here, at the only place we can. */
const customSizeSchema = sizeOf(fixedOrFill, fixedOrFill);
const progressSizeSchema = sizeOf(fixedOrFill, extent);

/** A GRID may not `hug` its width — a law S0f found by running the corpus on real shapers.
 *  Hug-grid intrinsic width = widest_cell x cols, and divide() then FLOORS, so column 0 is
 *  guaranteed fractionally narrower than the widest cell it must hold. Whether that cell's text
 *  wraps then hangs on sub-point shaper agreement (canvas said "Three"=39.2pt in a 39pt column
 *  and char-broke it; Minikin said it fits). A hug grid makes line_count NON-DETERMINISTIC
 *  ACROSS SHAPERS BY CONSTRUCTION — no tolerance can absorb a line-count flip. */
const gridSizeSchema = sizeOf(fixedOrFill, anySize.optional());

/** `image`/`video` may not `hug` their WIDTH (intrinsic sizing would depend on the bytes), but
 *  height-hug is LEGAL and is precisely the mechanism `aspect_ratio` exists for. */
const mediaSizeSchema = sizeOf(fixedOrFill, anySize.optional()).optional();

const paddingSchema = z
  .strictObject({
    top: extent,
    end: extent,
    bottom: extent,
    start: extent,
  })
  .optional();

const token = z.string().min(1);

/** Three motions, one field (SPEC §5.0e). Every bound is a PRODUCT bound, not a DoS one — the
 *  solver never reads any of this, so none of it can cost a frame. Under a second is a strobe;
 *  over five is a user waiting for a card. */
const animateSchema = z
  .strictObject({
    entry: z
      .strictObject({
        kind: z.enum(['fade', 'fade_up', 'scale_in']),
        duration_ms: z.number().int().min(50).max(5000),
        delay_ms: z.number().int().min(0).max(5000).optional(),
      })
      .optional(),
    stagger_ms: z.number().int().min(0).max(1000).optional(),
    parallax: z.number().min(-1).max(1).optional(),
  })
  .optional();

/** A gradient's STOPS ARE TOKENS, like every other colour on the wire (SPEC §5.0c). Bounded at 8:
 *  a product bound, not a DoS one — nobody has shipped a nine-stop card. Evenly distributed, with
 *  no per-stop offsets: four renderers, four interpolation spaces, and no customer asking. */
const gradientSchema = z.strictObject({
  kind: z.literal('linear'),
  angle: z.number().min(0).max(360),
  stops: z.array(token).min(2).max(8),
});

/** One radius, or four. PHYSICAL corners — a rounded card does not mirror in RTL. */
const cornersSchema = z.strictObject({
  tl: extent,
  tr: extent,
  br: extent,
  bl: extent,
});

/** PAINT-token value shapes (ADR 0014), extracted so `style` fields and the matching `theme`
 *  families (`radii`/`elevations`/`gradients`) share ONE definition and cannot drift. */
const radiusValueSchema = z.union([extent, cornersSchema]);
// `x` may be NEGATIVE (a shadow cast to the left); `spread` may not (it is an extent).
const shadowObjectSchema = z.strictObject({
  x: z.number().optional(),
  y: z.number(),
  blur: extent,
  spread: extent.optional(),
  color: token,
});
/** A `gradients` token reference. Tagged so it is unambiguous against a bare colour-token `bg`. */
const gradientRefSchema = z.strictObject({ gradient: token });

/** All colours are TOKENS — a raw hex has no dark variant, so raw values would silently kill
 *  dark mode and void the `resolved` gate. `checkTokenRefs` proves every one resolves. */
const styleSchema = z
  .strictObject({
    bg: z.union([token, gradientSchema, gradientRefSchema]).optional(),
    radius: z.union([radiusValueSchema, token]).optional(),
    border: z.strictObject({ width: extent, color: token }).optional(),
    opacity: z.number().min(0).max(1).optional(),
    shadow: z.union([shadowObjectSchema, token]).optional(),
    // CAPABILITY-GATED (SPEC §5.0e). The gate itself is enforced document-side, in
    // checkGatedFields — it needs `requires`/`fallback`, which live on the BLOCK, not here.
    backdrop: z.strictObject({ blur: z.number().min(0).max(100) }).optional(),
    animate: animateSchema,
  })
  .optional();

const alignSchema = z.enum(['start', 'center', 'end', 'stretch']);
const justifySchema = z.enum(['start', 'center', 'end', 'space_between']);

/** A `ref` span's `fallback` is REQUIRED: the value may be absent at runtime, and a hole in a
 *  sentence is not acceptable. No formatting in v1 — raw interpolation only (SPEC §11.1). */
const spanSchema = z.union([
  z.strictObject({ kind: z.literal('lit'), value: z.string().max(MAX_SPAN_CHARS) }),
  z.strictObject({
    kind: z.literal('ref'),
    path: z.string().min(1),
    fallback: z.string().max(MAX_SPAN_CHARS),
  }),
]);

const spansSchema = z.array(spanSchema).min(1).max(MAX_SPANS);
export const mediaSourceSchema: z.ZodType<UrlMediaSource> = z.strictObject({
  kind: z.literal('url'),
  url: z.string(),
});

/** Data-only, tagged on `type`. At publish an unknown type is a typo (rejected here); in the
 *  field it is the future (the SDK NO-OPS it and continues — never a crash, never a dropped
 *  button). Layout-neutral: the solver never reads actions. */
export const actionSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('dismiss') }),
  z.strictObject({ type: z.literal('open_url'), url: z.string().min(1) }),
  z.strictObject({ type: z.literal('deep_link'), url: z.string().min(1) }),
  z.strictObject({
    type: z.literal('track_event'),
    name: z.string().min(1),
    properties: z.record(z.string(), z.string()).optional(),
  }),
  z.strictObject({ type: z.literal('flow_next') }),
  z.strictObject({ type: z.literal('flow_back') }),
  z.strictObject({ type: z.literal('launch_experience'), campaign_id: z.string().min(1) }),
  // The host escape hatch (SPEC §11.6). `params` is string→string like `track_event.properties`:
  // a typed payload would be a second schema the host and the wire must agree on forever.
  z.strictObject({
    type: z.literal('host_callback'),
    name: z.string().min(1).max(100),
    params: z.record(z.string(), z.string()).optional(),
  }),
]);

/** Build the recursive grammar around a caller-owned media source. The SDK instantiation below is
 *  URL-only; the authoring schema instantiates the same blocks with its managed-source union. */
export function createBlockSchema<Source extends { kind: string }>(
  sourceSchema: z.ZodType<Source>
): z.ZodType<BlockNodeWithMedia<Source>> {
// The recursive self-reference. Declared once so every block below is a PLAIN strictObject —
// z.lazy() at each block would hide the `block` literal from z.discriminatedUnion.
const blockRef: z.ZodType<BlockNodeWithMedia<Source>> = z.lazy(() => recursiveBlockSchema);

// Every block spreads this. `requires[]` MUST be understood by the v1.0.0 decoder or it can
// never gate anything, ever (SPEC §11.5).
const base = {
  id: z.string().min(1),
  style: styleSchema,
  requires: z.array(z.string()).max(8).optional(),
  // ANY block is tappable (SPEC §5.0). The <=4 bound moved up with the field.
  actions: z.array(actionSchema).max(4).optional(),
  // A11Y (SPEC §5.0a). A tappable block becomes ONE accessibility element; `a11y_hidden` is the
  // explicit "this is decorative" marker. Neither moves a rectangle, so ONLY the coverage test and
  // each renderer's own suite can gate them.
  a11y_label: z.string().min(1).max(MAX_SPAN_CHARS).optional(),
  a11y_hidden: z.boolean().optional(),
};

// ---------------------------------------------------------------------------
// The 12 blocks.
// ---------------------------------------------------------------------------

const stackSchema =
  z.strictObject({
    ...base,
    block: z.literal('stack'),
    direction: z.enum(['row', 'column']),
    gap: extent,
    padding: paddingSchema,
    align: alignSchema,
    justify: justifySchema,
    size: anySizeSchema,
    children: z.array(blockRef).max(MAX_LAYOUT_NODES),
    fallback: blockRef.optional(),
  });

const boxSchema =
  z.strictObject({
    ...base,
    block: z.literal('box'),
    padding: paddingSchema,
    align_x: alignSchema,
    align_y: alignSchema,
    size: anySizeSchema,
    children: z.array(blockRef).max(MAX_LAYOUT_NODES),
    fallback: blockRef.optional(),
  });

const scrollSchema =
  z
    .strictObject({
      ...base,
      block: z.literal('scroll'),
      axis: z.enum(['vertical', 'horizontal']),
      snap: z.boolean().optional(),
      // Bounded on both ends: under a second is a strobe, over a minute is not a carousel. Layout
      // never reads it, so this bound is a PRODUCT bound, not a DoS one.
      auto_advance_ms: z.number().int().min(1000).max(60_000).optional(),
      size: anySizeSchema,
      children: z.array(blockRef).max(MAX_LAYOUT_NODES),
      fallback: blockRef.optional(),
    })
    .superRefine((n, ctx) => {
      // Auto-advance with nothing to advance TO is a config that silently does nothing — which is
      // exactly the kind of thing an author stares at for an hour. Reject it at publish.
      if (n.auto_advance_ms !== undefined && !n.snap) {
        ctx.addIssue({
          code: 'custom',
          path: ['auto_advance_ms'],
          message: `"${n.id}": auto_advance_ms needs snap:true — there are no snap points to advance between`,
        });
      }

      // `fill` ALONG the axis is ONE PAGE — the scroll's own extent (SPEC §6.7). That is what makes
      // a full-bleed carousel expressible: a card cannot be authored "as wide as the slot" when the
      // slot is 358pt on one phone and 430 on another.
      //
      // But a page needs an extent to BE, so the scroll must have one. A `hug` scroll takes its
      // along-axis size FROM its children, and a `fill` child would take its size from the scroll:
      // circular, and it would silently resolve to 0 — an invisible carousel, which is the worst
      // possible outcome and exactly the thing the gatekeeper exists to make impossible.
      const axisKey = n.axis === 'vertical' ? 'height' : 'width';
      const own = n.size?.[axisKey] ?? 'hug';
      const bounded = own !== 'hug';

      n.children.forEach((c, i) => {
        if (c.size?.[axisKey] === 'fill' && !bounded) {
          ctx.addIssue({
            code: 'custom',
            path: ['children', i, 'size', axisKey],
            message: `"${c.id}": ${axisKey}:"fill" means one page — but "${n.id}" hugs its ${n.axis} axis, so a page has no size. Give the scroll a fixed or fill ${axisKey}.`,
          });
        }
      });
    });

const gridSchema =
  z
    .strictObject({
      ...base,
      block: z.literal('grid'),
      cols: z.number().int().min(1).max(MAX_GRID_COLS),
      h_spacing: extent,
      v_spacing: extent,
      padding: paddingSchema,
      size: gridSizeSchema,
      children: z.array(blockRef).max(MAX_LAYOUT_NODES),
      fallback: blockRef.optional(),
    })
    .superRefine((n, ctx) => {
      // A grid cell's height is its own, or explicitly fixed. `fill` has no remainder to
      // divide and would silently resolve to 0 (SPEC §6.8).
      n.children.forEach((c, i) => {
        if (c.size?.height === 'fill') {
          ctx.addIssue({
            code: 'custom',
            path: ['children', i, 'size', 'height'],
            message: `"${c.id}": height:"fill" is invalid in a grid — use an explicit height for a uniform tile grid`,
          });
        }
      });
    });

const textSchema =
  z.strictObject({
    ...base,
    block: z.literal('text'),
    spans: spansSchema,
    font: token,
    color: token,
    align: z.enum(['start', 'center', 'end']).optional(),
    // A cap on the COUNT (SPEC §6.11). Min 1 — `max_lines: 0` is a text node that renders nothing,
    // which is a deleted block, not a layout. The upper bound is a product bound, not a DoS one:
    // the solver never allocates per line.
    max_lines: z.number().int().min(1).max(MAX_LINES).optional(),
    // PAINT-ONLY (SPEC §5.5) — no port may add it to an advance.
    decoration: z.enum(['underline', 'strike']).optional(),
    // Applied to the RESOLVED string before measurement, locale-invariantly (SPEC §6.4c). Per-node
    // rather than per-span because a `ref`'s value is only known at runtime.
    text_case: z.enum(['upper', 'lower', 'title']).optional(),
    size: anySizeSchema,
    fallback: blockRef.optional(),
  });

const imageSchema =
  z
    .strictObject({
      ...base,
      block: z.literal('image'),
      source: sourceSchema,
      // REQUIRED. Intrinsic sizing would make layout depend on the image bytes: the box would
      // jump on decode, and the harness could not state an expected rect without the asset.
      aspect_ratio: z.number().positive(),
      fit: z.enum(['cover', 'contain', 'fill']),
      radius: z.union([extent, token]).optional(),
      tint: token.optional(),
      // Normalised 0..1 of the overflow — CSS `object-position` semantics (SPEC §5.0d).
      focal: z.strictObject({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).optional(),
      size: mediaSizeSchema,
      fallback: blockRef.optional(),
    })
    .superRefine((n, ctx) => {
      // Nothing is cropped unless it COVERS, so there is nothing for a focal point to choose. An
      // author who sets one on a `contain` image is expecting something to happen; publish is the
      // only place to tell them it will not.
      if (n.focal !== undefined && n.fit !== 'cover') {
        ctx.addIssue({
          code: 'custom',
          path: ['focal'],
          message: `"${n.id}": focal needs fit:"cover" — nothing is cropped at fit:"${n.fit}", so there is nothing to choose`,
        });
      }
    });

const videoSchema =
  z.strictObject({
    ...base,
    block: z.literal('video'),
    source: sourceSchema,
    aspect_ratio: z.number().positive(),
    autoplay: z.boolean().optional(),
    muted: z.boolean().optional(),
    loop: z.boolean().optional(),
    poster: z.string().optional(),
    radius: z.union([extent, token]).optional(),
    size: mediaSizeSchema,
    fallback: blockRef.optional(),
  });

const buttonSchema =
  z.strictObject({
    ...base,
    block: z.literal('button'),
    label: spansSchema,
    variant: token,
    font: token,
    size: anySizeSchema,
    fallback: blockRef.optional(),
  });

const spacerSchema =
  z.strictObject({
    ...base,
    block: z.literal('spacer'),
    size: anySizeSchema,
    fallback: blockRef.optional(),
  });

const customSchema =
  z.strictObject({
    ...base,
    block: z.literal('custom'),
    identifier: z.string().min(1),
    // Lazily typed: the host names its own struct. No union type for customer config ever
    // enters our schema.
    config: z.record(z.string(), z.unknown()).optional(),
    // REQUIRED and fixed-or-fill on BOTH axes — see SPEC §8. This single line is what keeps
    // `custom` from destroying the solver's purity.
    size: customSizeSchema,
    fallback: blockRef.optional(),
  });

const progressSchema =
  z
    .strictObject({
      ...base,
      block: z.literal('progress'),
      segments: z.number().int().min(1).max(MAX_PROGRESS_SEGMENTS),
      value: z.number().int().min(0),
      gap: extent,
      // Fields, not magic strings. Hard-coding 'color.primary'/'color.muted' in the solver
      // would make two token names load-bearing in four pinned binaries forever.
      fill_color: token,
      track_color: token,
      /** The id of a `scroll` in this document — makes this its page indicator (SPEC §6.10). The
       *  reference is resolved at the DOCUMENT level (checkProgressTracks): a block cannot see its
       *  siblings, and a `tracks` pointing at nothing would leave dots that never move. */
      tracks: z.string().min(1).optional(),
      size: progressSizeSchema,
      fallback: blockRef.optional(),
    })
    .superRefine((n, ctx) => {
      if (n.value > n.segments) {
        ctx.addIssue({
          code: 'custom',
          path: ['value'],
          message: `value ${n.value} exceeds segments ${n.segments}`,
        });
      }
    });

const repeatSchema =
  z
    .strictObject({
      ...base,
      block: z.literal('repeat'),
      // A tagged union so {kind:"rest"} slots in later with ZERO schema break, and old SDKs
      // hit the fallback (SPEC §11.3).
      // A tagged union so {kind:"rest"} slots in later with ZERO schema break, and old SDKs hit
      // the fallback (SPEC §11.3). Both arms are a KEY INTO RenderCtx.items.
      source: z.discriminatedUnion('kind', [
        z.strictObject({ kind: z.literal('campaigns'), collection: z.string().min(1) }),
        // STATIC ITEMS, NOT A DATA PLANE: the host pushes a list once with setItems(). No fetch,
        // no reactivity — see RepeatSource in ir.ts.
        z.strictObject({ kind: z.literal('host'), key: z.string().min(1) }),
      ]),
      template: blockRef,
      max: z.number().int().min(1).max(MAX_REPEAT),
      size: anySizeSchema,
      fallback: blockRef.optional(),
    })
    .superRefine((n, ctx) => {
      // The expansion pass is not re-entrant.
      const hasRepeat = (b: BlockNodeWithMedia<Source>): boolean =>
        b.block === 'repeat' ||
        ('children' in b && b.children.some(hasRepeat)) ||
        (b.fallback ? hasRepeat(b.fallback) : false);
      if (hasRepeat(n.template)) {
        ctx.addIssue({
          code: 'custom',
          path: ['template'],
          message: 'a repeat template may not contain another repeat',
        });
      }
    });

const recursiveBlockSchema = z.discriminatedUnion('block', [
  stackSchema,
  boxSchema,
  scrollSchema,
  gridSchema,
  textSchema,
  imageSchema,
  videoSchema,
  buttonSchema,
  spacerSchema,
  customSchema,
  progressSchema,
  repeatSchema,
]) as z.ZodType<BlockNodeWithMedia<Source>>;
return recursiveBlockSchema;
}

export const blockSchema: z.ZodType<BlockNode> = createBlockSchema(mediaSourceSchema);

// ---------------------------------------------------------------------------
// Theme + document.
// ---------------------------------------------------------------------------

export const themeSchema = z.strictObject({
  fonts: z.record(
    z.string(),
    z.strictObject({
      family: z.string().min(1),
      // Fonts are BUNDLED, not system: Android ships no Arial and silently falls back to
      // Roboto, which would end line_count as an exact gate forever (SPEC §4.1).
      src: z.string().optional(),
      size: z.number().positive().max(200),
      weight: z.number().int().min(100).max(900),
      line_height: z.number().positive().max(4),
      max_scale: z.number().min(1).max(4),
      // A REAL FACE, supplied through `src` — synthetic oblique is forbidden (SPEC §4.1).
      style: z.enum(['normal', 'italic']).optional(),
      // POINTS, bounded both ways (SPEC §13). Negative tracking is real for tight display type;
      // past about −2pt at body sizes the glyphs collide, and past +10pt a heading fits no phone.
      // Both ends are also near enough to catch a mistyped unit — `16` meaning "16% of an em".
      letter_spacing: z.number().min(-2).max(10).optional(),
    })
  ),
  colors: z.record(
    z.string(),
    // A light/dark PAIR, always. This is why raw colour values are not permitted on the wire.
    z.strictObject({ light: z.string().min(1), dark: z.string().min(1) })
  ),
  buttons: z.record(
    z.string(),
    // A COMPONENT token composes FOUNDATION tokens (ADR 0014): bg is a colour token OR a gradient
    // token; radius is a literal OR a `radii` token; elevation is an optional `elevations` token.
    z.strictObject({
      bg: z.union([token, gradientRefSchema]).nullable(),
      fg: token,
      radius: z.union([radiusValueSchema, token]),
      padding: z.strictObject({
        top: extent,
        end: extent,
        bottom: extent,
        start: extent,
      }),
      elevation: token.optional(),
    })
  ),
  // PAINT-token families (ADR 0014). `.default({})` so a theme that predates them still parses —
  // the migration is additive, and an empty family is valid. Values reuse the `style` shapes above,
  // so a token and its inline counterpart cannot drift.
  radii: z.record(z.string(), radiusValueSchema).default({}),
  elevations: z.record(z.string(), shadowObjectSchema).default({}),
  gradients: z.record(z.string(), gradientSchema).default({}),
});

/** ONE definition, imported by the authoring doc and the wire flow alike. Three hand-copied surface
 *  enums is how one of them ends up missing `inline` and an author picks a surface that publishes
 *  into a void. */
export const surfaceSchema = z.enum(['modal', 'fullscreen', 'banner', 'bottom_sheet', 'inline']);
export type SurfaceKind = z.infer<typeof surfaceSchema>;

const documentSchema = z.strictObject({
  schema_version: z.literal('v1'),
  surface: surfaceSchema,
  theme: themeSchema,
  root: blockSchema,
});

/** Every token reference must RESOLVE. Not polish: an unknown token would render a documented
 *  default in the field forever, silently — the wrong colour on every install, with nothing to
 *  catch it. Publish is the only place this can be caught (SPEC §10). */
function checkTokenRefs(doc: ExperienceDocument, ctx: z.RefinementCtx): void {
  const { fonts, colors, buttons, radii, elevations, gradients } = doc.theme;

  const colour = (t: string | undefined, where: string): void => {
    if (t !== undefined && !(t in colors)) {
      ctx.addIssue({ code: 'custom', message: `unknown colour token "${t}" (${where})` });
    }
  };
  // Paint-token references (ADR 0014): radii/elevations/gradients keys on style fields.
  const paintToken = (dict: Record<string, unknown>, t: string, kind: string, where: string): void => {
    if (!(t in dict)) {
      ctx.addIssue({ code: 'custom', message: `unknown ${kind} token "${t}" (${where})` });
    }
  };

  // A button variant COMPOSES foundation tokens (ADR 0014) — every one must resolve, and a broken
  // one is just as fatal and just as invisible to the block's own parse.
  for (const [name, v] of Object.entries(buttons)) {
    colour(v.fg, `buttons.${name}.fg`);
    if (typeof v.bg === 'string') colour(v.bg, `buttons.${name}.bg`);
    else if (v.bg) paintToken(gradients, v.bg.gradient, 'gradient', `buttons.${name}.bg.gradient`);
    if (typeof v.radius === 'string') paintToken(radii, v.radius, 'radius', `buttons.${name}.radius`);
    if (v.elevation !== undefined) paintToken(elevations, v.elevation, 'elevation', `buttons.${name}.elevation`);
  }
  // Elevation and gradient tokens carry colour tokens INSIDE them (like `buttons`) — a broken one is
  // just as fatal, and just as invisible to the block's own parse.
  for (const [name, e] of Object.entries(elevations)) colour(e.color, `elevations.${name}.color`);
  for (const [name, g] of Object.entries(gradients)) {
    g.stops.forEach((t, i) => colour(t, `gradients.${name}.stops[${i}]`));
  }

  const walk = (n: BlockNode): void => {
    // `bg` is a token OR a gradient whose STOPS are tokens. A walk that only handled the string
    // would let an unresolvable stop through — and an unknown token renders a documented default
    // in the field FOREVER, silently, on the largest surface in the document.
    const bg = n.style?.bg;
    if (typeof bg === 'string') {
      colour(bg, `${n.id}.style.bg`);
    } else if (bg && 'gradient' in bg) {
      paintToken(gradients, bg.gradient, 'gradient', `${n.id}.style.bg.gradient`);
    } else if (bg) {
      bg.stops.forEach((t, i) => colour(t, `${n.id}.style.bg.stops[${i}]`));
    }
    colour(n.style?.border?.color, `${n.id}.style.border.color`);

    // `shadow` is an inline shadow (check its colour) OR an `elevations` token key.
    const shadow = n.style?.shadow;
    if (typeof shadow === 'string') {
      paintToken(elevations, shadow, 'elevation', `${n.id}.style.shadow`);
    } else if (shadow) {
      colour(shadow.color, `${n.id}.style.shadow.color`);
    }
    // `radius` is a `radii` token key when it is a string (a number/Corners resolves nothing).
    if (typeof n.style?.radius === 'string') {
      paintToken(radii, n.style.radius, 'radius', `${n.id}.style.radius`);
    }

    switch (n.block) {
      case 'text':
        colour(n.color, `${n.id}.color`);
        if (!(n.font in fonts)) {
          ctx.addIssue({ code: 'custom', message: `unknown font token "${n.font}" (${n.id}.font)` });
        }
        break;
      case 'button':
        if (!(n.variant in buttons)) {
          ctx.addIssue({
            code: 'custom',
            message: `unknown button variant "${n.variant}" (${n.id}.variant)`,
          });
        }
        if (!(n.font in fonts)) {
          ctx.addIssue({ code: 'custom', message: `unknown font token "${n.font}" (${n.id}.font)` });
        }
        break;
      case 'image':
        colour(n.tint, `${n.id}.tint`);
        if (typeof n.radius === 'string') paintToken(radii, n.radius, 'radius', `${n.id}.radius`);
        break;
      case 'video':
        if (typeof n.radius === 'string') paintToken(radii, n.radius, 'radius', `${n.id}.radius`);
        break;
      case 'progress':
        colour(n.fill_color, `${n.id}.fill_color`);
        colour(n.track_color, `${n.id}.track_color`);
        break;
      case 'repeat':
        walk(n.template);
        break;
      default:
        break;
    }

    if ('children' in n) n.children.forEach(walk);
    if (n.fallback) walk(n.fallback);
  };

  walk(doc.root);
}

/** Root schema for `experiences.document`. Bounds on the RAW input first (a recursive parse of a
 *  hostile 2000-deep tree blows the stack), then the tree, then the cross-tree token law. */
/** A `progress` that `tracks` a scroll is that carousel's DOTS. Two ways for it to lie, both of them
 *  cross-tree and therefore invisible to the block's own parse — and both of them silent on a device:
 *
 *    1. `tracks` naming nothing (or naming a `stack`): the dots never move, forever, and the author's
 *       only clue is that they never move.
 *    2. `segments` disagreeing with the number of cards: three dots over four cards, so the last card
 *       lights nothing and the indicator is a lie about how much is left.
 *
 *  Publish is the only place either can be caught. */
function checkProgressTracks(doc: ExperienceDocument, ctx: z.RefinementCtx): void {
  const scrolls = new Map<string, number>();
  const indicators: ProgressNode[] = [];

  const walk = (n: BlockNode): void => {
    if (n.block === 'scroll') scrolls.set(n.id, n.children.length);
    if (n.block === 'progress' && n.tracks !== undefined) indicators.push(n);
    if ('children' in n) n.children.forEach(walk);
    if (n.block === 'repeat') walk(n.template);
    if (n.fallback) walk(n.fallback);
  };
  walk(doc.root);

  for (const p of indicators) {
    const pages = scrolls.get(p.tracks!);
    if (pages === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `"${p.id}": tracks "${p.tracks}", which is not a scroll in this document`,
      });
      continue;
    }
    if (pages !== p.segments) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `"${p.id}": has ${p.segments} segments but tracks a scroll with ${pages} cards — the dots would lie`,
      });
    }
  }
}

/** THE GATED-FIELD LAW (SPEC §5.0e), and the two motion laws that need to see the TREE.
 *
 *  `backdrop` is the only style field whose absence is WORSE than its fallback: a binary that
 *  cannot blur would paint a card with no background at all, floating over the host app. So it is
 *  capability-gated, and the gate is not optional — a `backdrop` without `requires:
 *  ['backdrop_blur']` is a card that silently breaks on every device that cannot blur, and a
 *  `requires` without a `fallback` is a card that silently VANISHES there (§7.1: unsatisfied and
 *  no fallback means dropped). Publish is the only place either can be caught.
 *
 *  `parallax` and `stagger_ms` are the other half: both are fields that would simply DO NOTHING,
 *  forever, in a document that reads perfectly well. An author would stare at a still card for an
 *  hour. Neither can be seen from the block alone — parallax needs an ancestor, stagger needs
 *  children — so both live here. */
function checkGatedFields(doc: ExperienceDocument, ctx: z.RefinementCtx): void {
  const BACKDROP = 'backdrop_blur';

  const walk = (n: BlockNode, inScroll: boolean): void => {
    const s = n.style;

    if (s?.backdrop) {
      if (!n.requires?.includes(BACKDROP)) {
        ctx.addIssue({
          code: 'custom',
          path: ['root'],
          message: `"${n.id}": style.backdrop needs requires:["${BACKDROP}"] — without the gate this card renders with NO BACKGROUND on every device that cannot blur`,
        });
      }
      if (!n.fallback) {
        ctx.addIssue({
          code: 'custom',
          path: ['root'],
          message: `"${n.id}": a gated block needs a fallback — unsatisfied and without one it is DROPPED (§7.1), so the card vanishes instead of degrading to a solid bg`,
        });
      }
    }

    if (s?.animate?.parallax !== undefined && !inScroll) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `"${n.id}": parallax is SCROLL-DRIVEN — it translates against an ancestor scroll's offset, and there is no scroll above this block, so it would never move`,
      });
    }

    if (s?.animate?.stagger_ms !== undefined && !('children' in n)) {
      ctx.addIssue({
        code: 'custom',
        path: ['root'],
        message: `"${n.id}": stagger_ms belongs on a CONTAINER — it delays each CHILD in turn, and this block has none`,
      });
    }

    const nowInScroll = inScroll || n.block === 'scroll';
    if ('children' in n) n.children.forEach((c) => walk(c, nowInScroll));
    if (n.block === 'repeat') walk(n.template, nowInScroll);
    if (n.fallback) walk(n.fallback, inScroll);
  };

  walk(doc.root, false);
}

/** A ROOT `height: "fill"` MEANS THE VIEWPORT (SPEC §6.1c) — and only a `fullscreen` step has a
 *  viewport to fill.
 *
 *  This cannot live on the block: `surface` and `root` only meet at the document, which is exactly
 *  why it sits here beside the other three cross-tree laws. And it is not a matter of taste — every
 *  chrome, web and native, anchors its surface off the ROOT'S OWN SOLVED HEIGHT:
 *
 *      bottom_sheet:  originY = screenHeight - documentHeight        (Surfaces.swift)
 *
 *  So a viewport-tall root on a sheet gives `originY = 0`: the sheet renders at the TOP of the
 *  screen, with its detents collapsed onto one degenerate stop. The comment in `Surfaces.swift` says
 *  it plainly — "anchoring a viewport-tall box to the bottom edge is the same as anchoring it to the
 *  top, which is exactly how the web bug hid for as long as it did." A modal would centre to the same
 *  zero; a banner would be absurd; and an `inline` widget has no screen at all — the host hands it a
 *  width and the solver answers with a height, so the viewport is not the widget's to fill.
 *
 *  A root height that is a NUMBER stays legal on every surface: that is an author asking for a box of
 *  a known size, and the chrome anchors it correctly. Only `fill` is scoped. */
function checkRootFill(doc: ExperienceDocument, ctx: z.RefinementCtx): void {
  if (doc.root.size?.height !== 'fill' || doc.surface === 'fullscreen') return;

  ctx.addIssue({
    code: 'custom',
    path: ['root', 'size', 'height'],
    message:
      doc.surface === 'inline'
        ? 'a root `height: "fill"` fills the VIEWPORT, and an inline widget has no viewport — the host gives it a width and the solver answers with its height'
        : `a root \`height: "fill"\` fills the viewport, which only a fullscreen step has — on a ${doc.surface} it would anchor the surface to the top of the screen`,
  });
}

export const experienceDocumentSchema = z
  .unknown()
  .superRefine(checkRawBounds)
  .pipe(
    documentSchema
      .superRefine(checkTokenRefs)
      .superRefine(checkProgressTracks)
      .superRefine(checkGatedFields)
      .superRefine(checkRootFill)
  );

export type { ExperienceDocument } from './ir.js';
